import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatTime } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, MapPin, Video, User } from 'lucide-react';
import {
  isUpcoming, isActionRequired, isPending, isHistory, statusLabel, statusTone, bookingMode, modeLabel,
} from '@/lib/experienceStatus';

const TABS = [
  { id: 'action', label: 'Action Required', match: isActionRequired },
  { id: 'pending', label: 'Pending', match: isPending },
  { id: 'upcoming', label: 'Upcoming', match: (b) => isUpcoming(b) },
  { id: 'history', label: 'History', match: (b) => isHistory(b) },
];

function ExperienceCard({ booking, onOpen }) {
  const mode = bookingMode(booking);
  return (
    <div
      onClick={() => onOpen(booking)}
      className="rounded-2xl border border-[#EFF0F2] bg-white p-4 cursor-pointer transition-all hover:border-[#E0E2E6] hover:shadow-[0_2px_14px_rgba(16,24,40,0.06)]"
      data-testid={`parent-experience-${booking.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-[#1F2937] font-['Outfit'] truncate">{booking.career_name}</p>
            <Badge className={`border-0 text-[10px] shrink-0 ${statusTone(booking)}`} data-testid={`status-badge-${booking.id}`}>
              {statusLabel(booking)}
            </Badge>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-sm text-[#6B7280] mt-1.5">
            <span className="flex items-center gap-1.5" data-testid={`child-name-${booking.id}`}>
              <User className="h-3.5 w-3.5 text-[#9CA3AF]" />{booking.child_name}
            </span>
            <span className="flex items-center gap-1.5">
              {mode === 'Offline' ? <MapPin className="h-3.5 w-3.5 text-[#9CA3AF]" /> : <Video className="h-3.5 w-3.5 text-[#9CA3AF]" />}
              {modeLabel(booking)}
            </span>
            {booking.mentor_name && <span className="truncate">{booking.mentor_name}</span>}
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            {booking.assigned_date ? formatDate(booking.assigned_date) : 'Date to be scheduled'}
            {booking.assigned_time_slot ? ` · ${formatTime(booking.assigned_time_slot)}` : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full h-8 px-3.5 text-xs border-[#E5E7EB] shrink-0" data-testid={`open-experience-${booking.id}`}>
          View Details
        </Button>
      </div>
    </div>
  );
}

export default function ParentExperiences({ bookings }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('upcoming');
  const defaulted = useRef(false);

  useEffect(() => {
    if (defaulted.current || !bookings.length) return;
    defaulted.current = true;
    if (bookings.some(isActionRequired)) setTab('action');
  }, [bookings]);

  const counts = TABS.reduce((acc, t) => { acc[t.id] = bookings.filter(t.match).length; return acc; }, {});
  const list = bookings
    .filter(TABS.find(t => t.id === tab).match)
    .sort((a, b) => (b.assigned_date || '').localeCompare(a.assigned_date || ''));
  const ordered = tab === 'upcoming' || tab === 'action' ? [...list].reverse() : list;

  return (
    <div data-testid="parent-experiences-section">
      <div className="flex gap-1 bg-[#F3F4F6] rounded-full p-1 w-fit mb-6 max-w-full overflow-x-auto md:overflow-visible [&::-webkit-scrollbar]:hidden" data-testid="parent-experience-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition-colors ${
              tab === t.id ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'
            }`}
            data-testid={`parent-tab-${t.id}`}
          >
            {t.label} ({counts[t.id]})
          </button>
        ))}
      </div>

      {ordered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-10 text-center" data-testid="parent-experiences-empty">
          <CalendarDays className="h-6 w-6 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-[#9CA3AF]">Nothing in this category yet.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {ordered.map(b => (
            <ExperienceCard key={b.id} booking={b} onOpen={(bk) => navigate(`/sessions/${bk.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}
